import React, {Component} from 'react';
import {injectIntl} from 'react-intl';
import UpdateTaskMutation from '../../../../../mutations/update_task_mutation.modern';
import Util from '../../../util/util';
import {dispatch, EVENT_ID} from '../../../../../containers/event_manager';
import {RichTextEditorDraftJsWrapper} from '../../rich-text-editor-draftjs-wrapper/RichTextEditorDraftJsWrapper';
import {withinCharacterLimitOrNotify} from '../../rich-text-editor-draftjs-wrapper/characterLimit';
import {hasTaskUpdatePermission} from '../../../util/PermissionsUtil';
import {TASK_MODAL_PLUGIN_TOGGLES} from '../taskModalPluginToggles';
import {EDITOR_SURFACE, trackEditorSaveFailed, trackEditorSaveSucceeded} from '../../../../../tracking/amplitude/editorEvents';

class DescriptionSection extends Component {
	constructor(props) {
		super(props);

		this.state = {
			showUploadOptions: false,
			taskHasDependencies:
				props.task.dependsOnThisTask &&
				props.task.thisTaskDependsOn &&
				props.task.dependsOnThisTask.edges.length > 0 &&
				props.task.thisTaskDependsOn.edges.length > 0,
		};
		this.onKeyPress = this.onKeyPress.bind(this);

		this.plateJsMentionsArray = props.project.projectPersons.edges
			.filter(projectPerson => {
				return projectPerson.node.person.active;
			})
			.map(projectPerson => {
				return {
					id: projectPerson.node.person.id,
					text: projectPerson.node.person.firstName + ' ' + projectPerson.node.person.lastName,
				};
			});
	}

	UNSAFE_componentWillMount() {
		document.addEventListener('mousedown', this.onMouseDown);
		/*
		 * Listener added on body because of the order in which components
		 * receive events. If it is set on document, the listener in root.js
		 * will get the event first, and close the modal before the event is
		 * heard in this component.
		 */
		document.body.addEventListener('keydown', this.onKeyPress);
	}

	componentWillUnmount() {
		document.removeEventListener('mousedown', this.onMouseDown);
		document.body.removeEventListener('keydown', this.onKeyPress);
	}

	onMouseDown = e => {
		if (this.uploadOptions && !this.uploadOptions.contains(e.target) && !this.fileUploadButton.contains(e.target)) {
			this.setState({showUploadOptions: false});
		}
	};

	onKeyPress(e) {
		if (e.key === 'Escape') {
			// Escape produces no click, so the Plate editor's click-outside save never runs
			// and the modal closes over the edit. This listener is on `document.body` so that
			// it runs before the conductor's own Escape handler tears the modal down.
			this.flushDescription();
		}
	}

	/**
	 * Commits whatever is in the Plate description right now.
	 *
	 * That editor saves on a click outside itself, which the closes that unmount it never
	 * produce — the backdrop dismisses on `mousedown`, Escape produces no click at all — so
	 * the edit is discarded unless the close asks for it first.
	 *
	 * Safe to call on any close, which is why no caller has to ask whether there is
	 * anything to save: the wrapper commits nothing for a read-only editor, and nothing for
	 * a description the user never touched.
	 */
	flushDescription() {
		this.descriptionEditor?.saveNow();
	}

	isEmptyEditorState(editorState) {
		try {
			const editorJSON = JSON.parse(editorState);
			if (editorJSON.blocks && editorJSON.blocks.length === 1) {
				const text = editorJSON.blocks[0].text;
				return !text;
			}
			return false;
		} catch {
			return false;
		}
	}

	onTaskDescriptionUpdateSuccess(result, editorState) {
		if (result.updateTask.errors && result.updateTask.errors.length === 1) {
			Util.checkForSageErrorAndShowModal(result.updateTask.errors, false);
		} else {
			dispatch(EVENT_ID.SHOW_CHANGES_SAVED);
		}
	}

	editTaskDescription(editorState) {
		const emptyDescriptionChange = !this.props.task.description && this.isEmptyEditorState(editorState);

		// Have to replace the key in the description as that seems to change between conversions
		const currentDescription = editorState?.replace(/"key":"[^"]*"/g, '"key":"newtext"');
		const currentOldDescription = this.props.task.description?.replace(/"key":"[^"]*"/g, '"key":"newtext"');
		// Skip storing if the description is the same
		if (currentOldDescription === currentDescription) return;

		if (!emptyDescriptionChange && this.props.task.description !== editorState) {
			// Refused before anything else happens, so an over-limit description leaves no trace:
			// no mutation, and no inline images queued for deletion. The text stays in the editor.
			const characterLimit = this.props.viewer?.company?.characterLimit;
			if (!withinCharacterLimitOrNotify(editorState, characterLimit, this.props.intl, EDITOR_SURFACE.description)) {
				return;
			}

			// Tell the modal which inline images just left, and what the description
			// now holds. It resolves both on close — deletion is deferred so that
			// removing an image and undoing does not cost the user their picture.
			this.props.onInlineImagesChanged?.('description', this.props.task.description, editorState);

			Util.CommitMutation(
				UpdateTaskMutation,
				{
					ids: [this.props.task.id],
					description: editorState,
				},
				result => {
					// A resolved mutation is not necessarily a saved description: an
					// updateTask error (a Sage rejection, say) comes back on the success
					// channel and is shown to the user as a failure. Counting it as a save
					// would inflate the very success rate this telemetry exists to measure.
					const rejected = result.updateTask.errors && result.updateTask.errors.length === 1;
					if (rejected) {
						trackEditorSaveFailed(EDITOR_SURFACE.description, 'mutation');
					} else {
						trackEditorSaveSucceeded(EDITOR_SURFACE.description);
					}
					this.onTaskDescriptionUpdateSuccess(result, editorState);
				},
				true,
				null,
				// Save-success rate is the metric this feeds, so the failures have to be
				// counted too. The fallback runs either way, so the existing correlation-ID
				// error modal and retry behaviour are untouched; this only observes.
				//
				// A scheduled retry is not a failure yet — onFailure says so by returning
				// true — and counting one there would report a save as lost that is about to
				// succeed on its own.
				(transaction, onFailure) => {
					const retryScheduled = onFailure(transaction);
					if (!retryScheduled) {
						trackEditorSaveFailed(EDITOR_SURFACE.description, 'mutation');
					}
				}
			);
		}
	}

	uploadFile() {
		let file = this._fileInput.files.item(0);

		const upload = [];

		upload.push({
			taskId: this.props.task.id,
			mimeType: file.type,
			name: file.name,
			file: file,
		});

		let coverFile = null;
		if (!this.props.task.coverFile) {
			coverFile = upload.find(f => {
				const ext = f.name.split('.').pop();
				return ext === 'png' || ext === 'jpg' || ext === 'gif';
			});
		}

		const onSuccess = result => {
			if (!this.props.task.coverFile && coverFile && result) {
				Util.CommitMutation(UpdateTaskMutation, {
					ids: [this.props.task.id],
					coverFileId: result.createFile.file.node.id,
				});
			}
		};
		Util.uploadFiles(upload, onSuccess, coverFile);
	}

	/**
	 * A task linked to a Jira Server issue keeps its description in Jira: the Jira Server
	 * notifier pushes comments to Jira but not the description, so an edit made here would
	 * never reach Jira. Jira Cloud pushes the description, so its tasks stay editable.
	 */
	isJiraServerDescription() {
		const {task, project} = this.props;
		return !!task.jiraId && !!project?.jiraServerProject;
	}

	render() {
		const {task, isTaskReadOnly} = this.props;

		return (
			<div
				className="task-modal-v3-description-section"
				data-cy="task-modal-description-section"
				data-userpilot={'task-modal-description'}
			>
				<div className="description-text">
					<RichTextEditorDraftJsWrapper
						id={'task-description'}
						data-cy={'task-description'}
						peopleMentions={this.plateJsMentionsArray}
						initialDraftJsContent={task.description || ''}
						onDraftJsChanged={this.editTaskDescription.bind(this)}
						saveRef={handle => (this.descriptionEditor = handle)}
						surface={EDITOR_SURFACE.description}
						maxHeight={'400px'}
						pluginToggles={TASK_MODAL_PLUGIN_TOGGLES}
						readOnly={isTaskReadOnly || this.isJiraServerDescription() || !hasTaskUpdatePermission()}
						imageAdapter={this.props.imageAdapter}
					/>
					<input
						type="file"
						data-cy="task-modal-file-upload"
						onClick={e => {
							e.target.value = null;
						}}
						onChange={this.uploadFile.bind(this)}
						ref={input => (this._fileInput = input)}
						disabled={isTaskReadOnly}
						className="hidden-file-input"
					/>
				</div>
			</div>
		);
	}
}

// `forwardRef` so the task modal can reach `flushDescription` on close — `injectIntl`
// otherwise hands back a ref to its own wrapper.
export default injectIntl(DescriptionSection, {forwardRef: true});
